import { useIntl } from "react-intl";
import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

//Components
import { Grid, Typography, Container, Paper, Divider } from "@mui/material";
import { NotificationManager } from 'Components/Notifications';
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import Filters from '../.components/filters';
import { NavLink } from "react-router-dom";
import cx from 'classnames';

//Helpers
import { getDateFormatted } from 'Core/data/Helpers';

//Actions
import {
    cameraLogsRequest,
    clearRedux,
} from 'Redux/actions';

// Constants
const ROWS_PER_PAGE = 20;

const CameraLogs = (props) => {
    const { messages } = useIntl();
    const { timezone } = props;
    const tableRef = useRef();

    const dispatch = useDispatch();
    const { loading, hasMore, Items, error, errorCode } = useSelector(state => state.cameraLogsRedux)

    const [last, setLast] = useState({ id: '20', name: '20' });
    const [esn, setEsn] = useState(props?.single ? props?.device?.esn || "" : "");

    //initial filter status
    const initialStateFilters = {
        limit: ROWS_PER_PAGE,
        from: 0,
        esn: props?.device?.esn || ''
    }

    //local states
    const [filters, setFilters] = useState(initialStateFilters);

    const [columns, setColumns] = useState([
        {
            key: "camerasn",
            width: 200,
            title: messages['vidfleet_camera_sm'],
            show: true,
            locked: true,
            resize: true,
            export: true,
            exportCell: "cameraSN",
            code: "vidfleet_camera_sm",
            render: (item) => {
                return (
                    <NavLink to={`/app/devices/overview/${item?.cameraSN}`} style={{ color: 'black' }}>
                        <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.cameraSN}</span>
                    </NavLink>
                )
            }
        },
        {
            key: "uploadedtime",
            title: messages['vidfleet_uploaded_time'],
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: "uploadedTime",
            exportFormat: "datetime",
            code: "vidfleet_uploaded_time",
            render: (item) => {
                return <span>{item?.uploadedTime && getDateFormatted(item?.uploadedTime, "date", "datetime", null, timezone)}</span>
            }
        },
        {
            key: "requesttime",
            title: messages['vidfleet_request_time'],
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: "createTime",
            exportFormat: "datetime",
            code: "vidfleet_request_time",
            render: (item) => {
                return <span>{item?.createTime && getDateFormatted(item?.createTime, "date", "datetime", null, timezone)}</span>
            }
        },
        {
            key: "description",
            title: messages['vidfleet_description'],
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: "description",
            code: "vidfleet_description",
            render: (item) => {
                return <div>{item.description}</div>
            }
        },
    ])

    const getData = (refresh) => {
        if (refresh) dispatch(clearRedux("CAMERA_LOGS", { Items: [], hasMore: false }));

        let currentFilters = {
            ...filters,
            limit: last.id,
            from: +0,
            hasMore: refresh ? false : hasMore,
            esn: esn,
        }
        setFilters({ ...currentFilters })
        dispatch(cameraLogsRequest(currentFilters))
        tableRef?.current?.conditionsHandler(currentFilters)
    }

    const handleChangeConditions = (tags, newConditions, total, newColumns, rows, sort, key, fromPreferences) => {
        // Sync local state from preferences when loaded from saved preferences
        if (fromPreferences && newConditions && newConditions?.limit) {
            setLast({ id: String(newConditions.limit), name: String(newConditions.limit) });
        }

        let currentFilters = {
            ...filters,
            limit: newConditions?.limit || last?.id,
            from: +0,
            hasMore: false,
            esn: esn,
        }

        dispatch(cameraLogsRequest(currentFilters));
    }

    useEffect(() => {
        return () => {
            dispatch(clearRedux("CAMERA_LOGS"));
        };
    }, [])


    useEffect(() => {
        if (error) {
            if (esn) {
                NotificationManager.error(
                    errorCode === 403 ? messages['vidfleet_the_esn_could_not_be_consulted'] : messages['vidfleet_the_esn_does_not_exist'],
                    messages['vidfleet_camera_logs'],
                    3000,
                    null,
                    null,
                    'error'
                );
            } else {
                NotificationManager.error(
                    messages['vidfleet_error_camera_logs'],
                    messages['vidfleet_camera_logs'],
                    3000,
                    null,
                    null,
                    'error'
                );
            }
            dispatch(clearRedux("CAMERA_LOGS", { error: null, errorCode: null }));
        }
    }, [error])

    return (
        <Container maxWidth={false} className={cx("vidfleet-camera-logs-tab", !props?.single ? "container-view" : "container-view-tab")}>
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: !props?.single ? "14px 18px" : 0, borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    {
                        !props?.single &&
                        <Grid item xs={6}>
                            <Typography variant="h5" gutterBottom component="div">
                                {messages['vidfleet_camera_logs']}
                            </Typography>
                        </Grid>
                    }
                    {
                        !props?.single &&
                        <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                            <Divider style={{ backgroundColor: '#999999' }} />
                        </div>
                    }
                    <DataViewInfiniteScroll
                        ref={tableRef}
                        name="pm-cameralogs-v1"
                        showTotalCount={false}
                        columns={columns}
                        exportConfig={{
                            fileName: "cameralogs",
                            useDataGrid: true,
                            disablePDF: true,
                        }}
                        uiConfig={{
                            topHeight: props?.single ? 286 : 200,
                            hideSplit: true,
                            hideFilters: true,
                        }}
                        externalDataConfig={{
                            loading,
                            data: Items,
                            onRefresh: () => getData(true),
                            nextToken: false,
                            totalCount: Items?.length || 0,
                            totalCountLoading: loading,
                        }}
                        searchConfig={{
                            disabled: true,
                        }}
                        selectionConfig={{
                            disabled: true,
                        }}
                        tableButtons={["export", 'orderColumn']}
                        extraFilters={() => {
                            return (
                                <Filters
                                    onlyLast
                                    setLast={setLast}
                                    last={last}
                                    onChangeFilters={() => getData()}
                                    setEsn={setEsn}
                                    esn={esn}
                                    disabled={loading}
                                    single={props?.single}
                                    module={props?.module}
                                    setModule={props?.setModule}
                                />
                            )
                        }}
                        infiniteScroll
                        onChangeColumns={(items) => setColumns(items)}
                        flexEnd
                        onChangeConditions={handleChangeConditions}
    />
                </Grid>
            </Paper>
        </Container>
    )
}

export default CameraLogs;
